import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import ErrorManagementService from '@/services/error-management';
import { useIsMobile } from '@/hooks/use-mobile';

const PAGE_SIZE = 10;

export type RelicWithProfile = {
  id: string;
  content: string;
  image_url?: string | null;
  thumbnail_url?: string | null;
  file_type?: string | null;
  image_version?: number | null;
  created_at: string;
  user_id: string;
  is_update: boolean;
  like_count: number;
  upload_progress?: number;
  moderation_status: string;
  expires_at?: string | null;
  is_relic: boolean;
  saved_at?: string | null;
  profiles: {
    username: string;
    profile_image_url?: string | null;
    is_admin: boolean;
    is_premium: boolean;
    combo_score: number;
  };
};

// Dedicated fetch function for relics only
const fetchRelicsPage = async (pageParam: number) => {
  const startIndex = pageParam * PAGE_SIZE;
  
  try {
    console.log('🔮 [RELICS] Fetching relics page:', pageParam);
    
    const { data, error } = await supabase
      .from('bulletins')
      .select(`
        *,
        profiles:profiles(username, profile_image_url, is_admin, is_premium, combo_score)
      `)
      .eq('is_relic', true)
      .in('moderation_status', ['approved', 'flagged'])
      .order('created_at', { ascending: false })
      .range(startIndex, startIndex + PAGE_SIZE - 1);

    if (error) {
      console.error('❌ [RELICS] Fetch error:', error);
      ErrorManagementService.handleQueryError(error);
      throw error;
    }

    console.log('✅ [RELICS] Fetched relics:', data?.length || 0);

    const items = (data || []).slice();

    return {
      items,
      nextPage: data?.length === PAGE_SIZE ? pageParam + 1 : undefined
    };
  } catch (error) {
    console.error('💥 [RELICS] Fetch failed:', error);
    ErrorManagementService.handleQueryError(error);
    throw error;
  }
};

export const useInfiniteRelics = (activeTab: 'pour-toi' | 'updates') => {
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  
  // Separate cache settings for relics
  const staleTime = 1000 * 60 * 5; // 5 minutes (longer for relics)
  const gcTime = 1000 * 60 * 15; // 15 minutes (longer retention)

  console.log('🔮 [RELICS HOOK] Initializing with activeTab:', activeTab);

  return {
    ...useInfiniteQuery({
      queryKey: ['infinite-relics', activeTab],
      queryFn: ({ pageParam = 0 }) => fetchRelicsPage(pageParam),
      getNextPageParam: (lastPage) => lastPage.nextPage,
      initialPageParam: 0,
      staleTime: staleTime,
      gcTime: gcTime,
      // More conservative refetch settings for relics
      refetchOnMount: true,
      refetchOnWindowFocus: false, // Disable for relics to reduce conflicts
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        const shouldRetry = failureCount < 2 && !ErrorManagementService.shouldReportError(error);
        if (shouldRetry) {
          const delay = ErrorManagementService.calculateBackoffDelay(failureCount);
          console.log(`🔄 [RELICS] Retrying after ${delay}ms (attempt ${failureCount + 1})`);
        }
        return shouldRetry;
      },
      meta: {
        errorContext: 'Fetching relics failed',
      }
    }),
    addOptimisticRelic: (newRelic: RelicWithProfile) => {
      console.log('➕ [RELICS CACHE] Adding optimistic relic:', {
        id: newRelic.id,
        content: newRelic.content?.substring(0, 50) + '...',
        timestamp: new Date().toISOString()
      });
      
      queryClient.setQueryData(
        ['infinite-relics', activeTab],
        (oldData: any) => {
          if (!oldData?.pages?.length) {
            console.log('⚠️ [RELICS CACHE] No existing pages, returning oldData');
            return oldData;
          }
          
          // Check if relic already exists
          const existingRelic = oldData.pages.flatMap((page: any) => page.items)
            .find((item: any) => item.id === newRelic.id);
            
          if (existingRelic) {
            console.log('⚠️ [RELICS CACHE] Relic already exists, skipping optimistic update');
            return oldData;
          }
          
          const updatedData = {
            ...oldData,
            pages: [{
              ...oldData.pages[0],
              items: [newRelic, ...oldData.pages[0].items]
            }, ...oldData.pages.slice(1)]
          };
          
          console.log('✅ [RELICS CACHE] Added relic to cache, new total count:', 
            updatedData.pages.flatMap((p: any) => p.items).length);
          
          return updatedData;
        }
      );
    },
    updateOptimisticRelic: (relicId: string, updates: Partial<RelicWithProfile>) => {
      console.log('🔄 [RELICS CACHE] Updating optimistic relic:', {
        id: relicId,
        updates: Object.keys(updates),
        timestamp: new Date().toISOString()
      });
      
      queryClient.setQueryData(
        ['infinite-relics', activeTab],
        (oldData: any) => {
          if (!oldData?.pages?.length) {
            console.log('⚠️ [RELICS CACHE] No pages for update');
            return oldData;
          }
          
          const updatedPages = oldData.pages.map((page: any) => ({
            ...page,
            items: page.items.map((item: RelicWithProfile) => 
              item.id === relicId ? { ...item, ...updates } : item
            )
          }));
          
          return {
            ...oldData,
            pages: updatedPages
          };
        }
      );
    },
    removeOptimisticRelic: (relicId: string) => {
      console.log('➖ [RELICS CACHE] Removing optimistic relic:', {
        id: relicId,
        timestamp: new Date().toISOString()
      });
      
      queryClient.setQueryData(
        ['infinite-relics', activeTab],
        (oldData: any) => {
          if (!oldData?.pages?.length) {
            console.log('⚠️ [RELICS CACHE] No pages for removal');
            return oldData;
          }
          
          const updatedData = {
            ...oldData,
            pages: oldData.pages.map((page: any) => ({
              ...page,
              items: page.items.filter((item: any) => item.id !== relicId)
            }))
          };
          
          console.log('✅ [RELICS CACHE] Relic removed, new total count:', 
            updatedData.pages.flatMap((p: any) => p.items).length);
          
          return updatedData;
        }
      );
    },
    fetchRelicById: async (relicId: string) => {
      try {
        console.log('🔍 [RELICS] Fetching relic by ID:', relicId);
        
        const { data, error } = await supabase
          .from('bulletins')
          .select(`
            *,
            profiles:profiles(username, profile_image_url, is_admin, is_premium, combo_score)
          `)
          .eq('id', relicId)
          .eq('is_relic', true)
          .maybeSingle();

        if (error) {
          console.error('❌ [RELICS] Fetch by ID error:', error);
          ErrorManagementService.handleQueryError(error);
          throw error;
        }

        // Update the cache with the fresh relic data or remove if missing
        if (data) {
          queryClient.setQueryData(
            ['infinite-relics', activeTab],
            (oldData: any) => {
              if (!oldData?.pages?.length) return oldData;
              const updatedPages = oldData.pages.map((page: any) => ({
                ...page,
                items: page.items.map((item: RelicWithProfile) => 
                  item.id === relicId ? { ...item, ...data } : item
                )
              }));
              return { ...oldData, pages: updatedPages };
            }
          );
        } else {
          // If the relic no longer exists, remove it from cache
          queryClient.setQueryData(
            ['infinite-relics', activeTab],
            (oldData: any) => {
              if (!oldData?.pages?.length) return oldData;
              const updatedPages = oldData.pages.map((page: any) => ({
                ...page,
                items: page.items.filter((item: RelicWithProfile) => item.id !== relicId)
              }));
              return { ...oldData, pages: updatedPages };
            }
          );
        }

        console.log('✅ [RELICS] Fetched relic by ID successfully');
        return data;
      } catch (error) {
        console.error('💥 [RELICS] Fetch by ID failed:', error);
        ErrorManagementService.handleQueryError(error);
        throw error;
      }
    }
  };
};